// PromptEditorPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Tooltip,
  CircularProgress,
  ExpansionPanelActions,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SaveIcon from "@material-ui/icons/Save";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import WaveTitle from "../SolutionGenerator/WaveTitle";
import Button from "components/CustomButtons/Button.js";
import { AccountCircle, FiberManualRecord } from "@material-ui/icons";
import PromptTextEditor from "./PromptTextEditor";
import {
  getPromptAPI,
  editPromptAPI,
  enhancePromptAPI,
  deployPromptAPI,
  getPromptVersionsAPI,
} from "views/APIs/PromptHubUtility";
import DiffViewer from "./DiffViewer";
import { Auth } from "aws-amplify";
import { fetchUserAPI } from "views/APIs/APIUtility";

/** ===== Styles ===== */
const useStyles = makeStyles((theme) => ({
  appBar: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  metaCard: {},
  label: { color: theme.palette.text.secondary, marginRight: theme.spacing(1) },
  editorBox: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },

  stepPaper: {
    padding: theme.spacing(2),
    borderRadius: 14,
  },
  diffBox: {
    width: "100%",
    overflowX: "auto",
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 13,
    lineHeight: 1.5,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  diffLine: {
    whiteSpace: "pre-wrap",
    padding: "2px 8px",
  },
  plus: { background: "rgba(76, 175, 80, 0.10)" },
  minus: { background: "rgba(244, 67, 54, 0.10)" },
  equal: {},
  subtle: { color: theme.palette.text.secondary, textTransform: "none" },
}));

/** ===== Simple line-by-line diff (naïve but effective for prompts) ===== */
function computeLineDiff(oldText = "", newText = "") {
  const oldLines = oldText.split("\n");
  const newLines = newText.split("\n");
  const max = Math.max(oldLines.length, newLines.length);
  const rows = [];
  for (let i = 0; i < max; i++) {
    const a = oldLines[i] ?? "";
    const b = newLines[i] ?? "";
    if (a === b) {
      rows.push({ type: "equal", text: "  " + b });
    } else {
      if (a) rows.push({ type: "minus", text: "- " + a });
      if (b) rows.push({ type: "plus", text: "+ " + b });
    }
  }
  return rows;
}

/** ===== Helper ===== */
function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** ===== Page ===== */
export default function PromptEditorPage() {
  if (localStorage.getItem("userCategory") !== "super_admin") {
    window.location.href = "/create/landing-page";
  }

  const { promptId } = useParams();
  const classes = useStyles();

  const [activeTab, setActiveTab] = useState(0);

  const [loading, setLoading] = useState(true);

  const [promptMeta, setPromptMeta] = useState(null); // Prompts collection doc
  const [versions, setVersions] = useState([]); // PromptVersions[] (newest first)
  const [currentText, setCurrentText] = useState(""); // editable text
  const [selectedVersion, setSelectedVersion] = useState(null); // currently loaded version object
  const [commitMessage, setCommitMessage] = useState("");

  const latestVersionObj = useMemo(() => {
    if (!versions.length) return null;
    return versions.reduce(
      (max, v) => (v.version > (max?.version ?? 0) ? v : max),
      versions[0]
    );
  }, [versions]);

  async function loadAll() {
    setLoading(true);
    try {
      if (!promptId) {
        setLoading(false);
        return;
      }
      const [meta, vers] = await Promise.all([
        getPromptAPI(promptId),
        getPromptVersionsAPI(promptId),
      ]);
      // Sort versions DESC by version number
      const sorted = [...vers].sort((a, b) => b.version - a.version);
      setPromptMeta(meta);
      setVersions(sorted);
      const start = sorted[0]; // latest
      setSelectedVersion(start);
      setCurrentText(start?.prompt ?? "");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, [promptId]);

  const handleLoadVersion = (vObj) => {
    setSelectedVersion(vObj);
    setCurrentText(vObj.prompt || "");
  };

  const getPrevVersionPrompt = (indexDesc) => {
    // get the version whose version matches indexdesc
    const prev = versions.find((v) => v.version === indexDesc - 1);
    const prevPrompt = prev?.prompt || "";
    return prevPrompt;
  };

  function stepIcon() {
    // if (currentVersion === deployedVersion) {
    //     return <MergeType style={{ backgroundColor: "#20677b", color: "white", borderRadius: 6 }} />;
    // }
    // if (currentVersion === latestVersion) {
    //     return <FiberNew color="primary" />;
    // }
    return <FiberManualRecord color="disabled" />;
  }

  if (loading || !promptMeta) {
    return (
      <>
        <Divider style={{ marginBottom: 16 }} />
        <Typography variant="h5">
          <WaveTitle text="Prompt Editor" />
        </Typography>
        <Typography variant="body1">
          Edit and manage your prompts here.
        </Typography>
        <Divider style={{ marginTop: 16, marginBottom: 16 }} />
        <Box>
          <Box display="flex" justifyContent="center" mt={8}>
            <CircularProgress style={{ fontSize: "20px" }} />{" "}
            <Typography variant="h6" style={{ marginLeft: "10px" }}>
              Loading...
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Divider style={{ marginBottom: 16 }} />
      <Typography variant="h5">
        Prompt Name: {promptMeta.name.replaceAll("_", " ")}
      </Typography>
      <Typography variant="body1">{promptMeta.description}</Typography>
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Box>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          aria-label="prompt editor tabs"
          style={{
            marginBottom: 16,
          }}
        >
          <Tab label="Editor" value={0} />
          <Tab label="Changelog" value={1} />
        </Tabs>

        {/* Editor column */}
        {activeTab === 0 && (
          <Box>
            <PromptTextEditor
              value={currentText}
              onChange={setCurrentText}
              requiredFields={promptMeta?.required_fields ?? []}
              application_name={promptMeta?.application_name}
              latest_version={promptMeta?.latest_version}
              deployed_version={promptMeta?.deployed_version}
              selectedVersion={selectedVersion?.version}
              promptId={promptId}
              loadAll={loadAll}
              latestVersionObj={latestVersionObj}
            />
          </Box>
        )}
        {activeTab === 1 && (
          <Paper className={classes.stepPaper}>
            <Typography
              variant="h6"
              style={{ textTransform: "none" }}
              gutterBottom
            >
              Changelog
            </Typography>
            <Divider style={{ marginBottom: 12 }} />
            <Stepper orientation="vertical" nonLinear>
              {versions.map((v, idx) => {
                const prevText = getPrevVersionPrompt(v.version);
                const diffRows = computeLineDiff(prevText, v.prompt);
                return (
                  <Step key={v._id} active>
                    <StepLabel StepIconComponent={stepIcon}>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: 600, textTransform: "none" }}
                        >
                          {v.commit_message || "No commit message"}
                        </Typography>
                        {promptMeta.deployed_version === v.version && (
                          <Chip
                            size="small"
                            style={{
                              backgroundColor: "#20677b",
                              color: "white",
                            }}
                            label="Deployed"
                          />
                        )}
                        {versions[0]?.version === v.version && (
                          <Chip
                            size="small"
                            color="textSecondary"
                            label="Latest"
                          />
                        )}
                        <Chip
                          size="small"
                          style={{
                            backgroundColor: "#20677b",
                            color: "white",
                            marginLeft: "auto",
                          }}
                          label={`v${v.version}.0`}
                        />
                      </Box>

                      <Typography
                        className={classes.subtle}
                        style={{
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <AccountCircle style={{ fontSize: "12px" }} />{" "}
                        {v.created_by} • {fmtDate(v.created_at)}
                      </Typography>
                    </StepLabel>
                    <StepContent style={{ marginBottom: "-16px" }}>
                      <ExpansionPanel elevation={0}>
                        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography>View Changes</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails>
                          <DiffViewer
                            oldText={getPrevVersionPrompt(v.version)}
                            newText={v.prompt}
                          />
                        </ExpansionPanelDetails>
                        <ExpansionPanelActions>
                          <Button
                            variant="outlined"
                            size="sm"
                            color="quCreateColor"
                            onClick={() => {
                              handleLoadVersion(v);
                              setActiveTab(0);
                            }}
                          >
                            Load this version
                          </Button>
                        </ExpansionPanelActions>
                      </ExpansionPanel>
                    </StepContent>
                  </Step>
                );
              })}
            </Stepper>
          </Paper>
        )}
      </Box>
    </>
  );
}
